import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Box, Divider, Paper, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Form, Formik } from "formik";
import {
  CustomButton,
  CustomCard,
  CustomIconButton,
  CustomRadioButtonGroupItem,
  DropzoneComponent,
  ReskinningInputs,
} from "../../../../../../../../Components";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import {
  CreatePaymentPlan,
  UpdatePaymentPlan,
} from "../../../../../../../../Services/PaymentPlanServices";
import { mapActiveItemToState, mapStateToCreatePaymentPlanDto } from "./models";
import { useVerticalNav } from "../../../../../../../../Contexts/VerticalNavContext";
import { GetFileTypeIcon } from "../../../../../../NewContactsView/ContactOverview/UI";
import { formatBytes } from "../../../../../../../../Helper";
import CustomlookupSelect from "../../../../../../../../Components/V2/CustomLookupSelect/CustomlookupSelect";
import InstallmentSection from "./InstallmentSection";
import HandoverSection from "./HandoverSection";
import FeesSection from "./FeesSection";

// icons
import { CloseXIcon, DeleteIcon } from "../../../../../../../../assets/icons";

// styles
import useStyles from "./styles";

const validationSchema = Yup.object().shape({
  paymentPlanName: Yup.string().required("This field is required"),
  paymentTypeId: Yup.number().required("This field is required"),
});

const PaymentPlanDialog = ({ propertyId, activeItem, onClose, reloadData }) => {
  const styles = useStyles();
  const bannerRef = useRef();
  const sentinelRef = useRef();

  const initialValues = {
    propertyPlanStatus: 1,
    paymentPlanName: "",
    paymentTypeId: null,
    description: "",
    isForSpecificProperty: !!propertyId,
    isForSpecificDeveloper: false,
    selectedConfigurationId: null,
    masterDevelopers: [],
    secondaryDevelopers: [],
    paymentPlanDocuments: [],
    downPaymentAmountPercentage: 0,
    handoverAmountPercentage: 0,
    installment: [],
    postHandoverInstallment: [],
    paymentPlanFees: [],
    propertyId: Number(propertyId),
  };

  const {
    theme: { palette },
  } = useSelectedTheme();
  const { translate } = useTranslate("NewPropertiesView");
  const { setAlertBoxContent } = useVerticalNav();

  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [acceptedFiles, setAcceptedFiles] = useState([]);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const totalPercentage = calculateTotalPercentage(values);
      if (totalPercentage !== 100) {
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("total-installments-should-be-100%"),
          onClose: () => setAlertBoxContent(null),
        });
        setIsLoading(false);
        return;
      }
      const body = mapStateToCreatePaymentPlanDto({
        ...values,
        paymentPlanDocuments: uploadedFiles.map((file) => ({
          fileId: file.fileId,
          fileName: file.fileName,
        })),
      });

      if (activeItem && activeItem.propertyPaymentPlanId) {
        await UpdatePaymentPlan(body);
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate("payment-plan-updated-successfully"),
          onClose: () => setAlertBoxContent(null),
        });
      } else {
        await CreatePaymentPlan(body);
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate("payment-plan-created-successfully"),
          onClose: () => setAlertBoxContent(null),
        });
      }
      onClose();
      reloadData && reloadData();
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("something-went-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotalPercentage = (values) => {
    let percentage = 0;
    percentage += Number(values.downPaymentAmountPercentage) || 0;
    percentage += Number(values.handoverAmountPercentage) || 0;

    if (Array.isArray(values.installment)) {
      values.installment.forEach((item) => {
        if (item.numberOfInstallment) {
          percentage +=
            (Number(item.amountPercentage) || 0) *
            Number(item.numberOfInstallment);
        } else {
          percentage += Number(item.amountPercentage) || 0;
        }
      });
    }

    if (Array.isArray(values.postHandoverInstallment)) {
      values.postHandoverInstallment.forEach((item) => {
        if (item.numberOfInstallment) {
          percentage +=
            (Number(item.amountPercentage) || 0) *
            Number(item.numberOfInstallment);
        } else {
          percentage += Number(item.amountPercentage) || 0;
        }
      });
    }

    return percentage;
  };

  const handleFilesAccepted = (files) => {
    setAcceptedFiles(files);
  };
  const handleDeleteUploadedFile = () => {
    setUploadedFiles([]);
  };

  const formatUploadedDocumentToMapp = () => {
    if (uploadedFileId && acceptedFiles.length > 0) {
      setUploadedFiles([
        {
          ...acceptedFiles[0],
          fileId: uploadedFileId,
          fileSize: acceptedFiles[0]?.size,
          fileName: acceptedFiles[0]?.path,
          fileType: acceptedFiles[0]?.type,
        },
      ]);
    }
  };

  useEffect(() => {
    if (activeItem && (activeItem.propertyPaymentPlanId || activeItem.id)) {
      setFormInitialValues(mapActiveItemToState(activeItem));
    } else {
      setFormInitialValues(initialValues);
    }
  }, [activeItem, propertyId]);

  useEffect(() => {
    formatUploadedDocumentToMapp();
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting);
      },
      { threshold: [0] }
    );
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => observer.disconnect();
  }, [uploadedFileId]);

  return (
    <Paper className={styles.modal}>
      <Box className={styles.header}>
        <Box className={styles.containerTitlePlusBackIcon}>
          <Typography variant="h6">
            {activeItem && activeItem.propertyPaymentPlanId
              ? translate("edit-custom-payment-plan")
              : translate("add-custom-payment-plan")}
          </Typography>
          <CustomIconButton
            variant="text"
            size="md"
            boxShadow="none"
            color="tertiaryColor"
            hideHoverBg
            onClick={onClose}
          >
            <CloseXIcon
              width="24"
              height="24"
              fill={palette.foreground.quinary}
            />
          </CustomIconButton>
        </Box>
        <Typography variant="h6" className={styles.subTitle}>
          {activeItem && activeItem.propertyPaymentPlanId
            ? translate("use-this-form-to-edit-the-payment-plan")
            : translate("use-this-form-to-add-a-new-payment-plan")}
        </Typography>
      </Box>
      <Box ref={sentinelRef} style={{ height: 1 }} />
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleSubmit, setFieldValue }) => {
          const totalPercentage = calculateTotalPercentage(values);
          return (
            <>
              <Alert
                ref={bannerRef}
                className={`${styles.banner} ${
                  isStuck ? styles.bannerStuck : ""
                }`}
                icon={false}
              >
                <Box className={styles.bannerBox}>
                  <Typography
                    style={{
                      fontWeight: "600",
                      color: palette.text.brand_secondary,
                    }}
                  >
                    {translate("total-installment")}
                  </Typography>
                  <Typography
                    style={{
                      fontWeight: "500",
                      color: palette.text.brand_secondary,
                    }}
                  >
                    {totalPercentage}%
                  </Typography>
                </Box>
              </Alert>
              <Box className={styles.wrapperContent}>
                <Form
                  onSubmit={handleSubmit}
                  className={styles.containerFormAddFinancePlan}
                >
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("status")}
                    </Typography>
                    <CustomRadioButtonGroupItem
                      checked={values.propertyPlanStatus === 1}
                      onChange={() => setFieldValue("propertyPlanStatus", 1)}
                      label={
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            color: palette.utility.success_500,
                          }}
                        >
                          <ul style={{ marginBottom: "3px", fontSize: "14px" }}>
                            <li>{translate("active")}</li>
                          </ul>
                        </Box>
                      }
                      value={"active"}
                    />

                    <CustomRadioButtonGroupItem
                      checked={values.propertyPlanStatus === 2}
                      onChange={() => setFieldValue("propertyPlanStatus", 2)}
                      label={
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            color: palette.utility.error_500,
                          }}
                        >
                          <ul style={{ marginBottom: "3px", fontSize: "14px" }}>
                            <li>{translate("inactive")}</li>
                          </ul>
                        </Box>
                      }
                      value={"inactive"}
                    />
                  </Box>
                  <Divider />
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("payment-plan-name")}
                    </Typography>
                    <ReskinningInputs
                      inputPlaceholder={translate(
                        "enter-plan-name e.g., ‘50/50 Payment Plan"
                      )}
                      style={{ maxWidth: "100%" }}
                      idRef="activitiesSearchRef"
                      value={values.paymentPlanName}
                      onInputChanged={(e) => {
                        setFieldValue("paymentPlanName", e.target.value);
                      }}
                      error={
                        touched.paymentPlanName &&
                        Boolean(errors.paymentPlanName)
                      }
                      helperText={
                        touched.paymentPlanName && errors.paymentPlanName
                      }
                    />
                  </Box>
                  <Divider />
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("payment-type")}
                    </Typography>
                    <CustomlookupSelect
                      lookupName={"PlanType"}
                      value={values.paymentTypeId}
                      onSelectChanged={(val) =>
                        setFieldValue("paymentTypeId", val)
                      }
                      renderValue={(_val, item) => (
                        <strong>{item?.lookupItemName}</strong>
                      )}
                      idRef="paymentTypeSelect"
                      placeholder={translate("select-payment-type")}
                      error={touched.paymentTypeId && errors.paymentTypeId}
                      helperText={touched.paymentTypeId && errors.paymentTypeId}
                    />
                  </Box>
                  <Divider />
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("description")}
                    </Typography>
                    <ReskinningInputs
                      inputPlaceholder={translate("add-details-or-comments")}
                      style={{ maxWidth: "100%" }}
                      value={values.description}
                      rows={4}
                      rowsMax={10}
                      multiline
                      idRef="activitiesSearchRef"
                      onInputChanged={(e) => {
                        setFieldValue("description", e.target.value);
                      }}
                    />
                  </Box>
                  <Divider />
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("attachment")}
                    </Typography>
                    <Box className={styles.dropzoneSection}>
                      <DropzoneComponent
                        setUploadedFileId={setUploadedFileId}
                        accept=".xls,.xlsx,.xlsm,.pdf,.doc,.docx,.jpg,.jpeg,.png,"
                        title={translate("SVG-PNG-JPG-or-GIF-max-800x400px")}
                        onFilesAccepted={handleFilesAccepted}
                      />
                      {uploadedFiles && uploadedFiles?.length > 0 && (
                        <Box className={styles.containerUploadedFilesCards}>
                          <CustomCard
                            boxShadow="xs"
                            borderRadius="xl"
                            borderColor="secondary"
                            classes={styles.CardUploadedFilesInfo}
                          >
                            {GetFileTypeIcon(uploadedFiles[0]?.fileType)}
                            <Box className={styles.ContainerFileUploadedInfo}>
                              <Box
                                className={styles.containerTitleAndDeleteIcon}
                              >
                                <Typography className={styles.TitleFileName}>
                                  {uploadedFiles[0]?.fileName}
                                </Typography>
                                <DeleteIcon
                                  width="20"
                                  height="20"
                                  className={styles.DeleteIcon}
                                  style={{ cursor: "pointer" }}
                                  fill={palette.button.secondary_fg}
                                  onClick={handleDeleteUploadedFile}
                                />
                              </Box>
                              <Typography className={styles.FileSizeTitle}>
                                {formatBytes(uploadedFiles[0]?.fileSize)}
                              </Typography>
                            </Box>
                          </CustomCard>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Divider />
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("down-payment")}
                    </Typography>
                    <ReskinningInputs
                      startAdornment={
                        <span className={styles.adornment}>%</span>
                      }
                      inputPlaceholder={translate(
                        "enter-upfront-payment e.g., 20%"
                      )}
                      style={{ maxWidth: "100%" }}
                      min={0}
                      type="number"
                      idRef="activitiesSearchRef"
                      value={values.downPaymentAmountPercentage}
                      onInputChanged={(e) => {
                        setFieldValue(
                          "downPaymentAmountPercentage",
                          e.target.value
                        );
                      }}
                    />
                  </Box>
                  {/* Installment Section */}
                  <InstallmentSection />
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("handover-payment")}
                    </Typography>
                    <ReskinningInputs
                      startAdornment={
                        <span className={styles.adornment}>%</span>
                      }
                      inputPlaceholder={translate(
                        "enter-handover-percentage(e.g., 30%)"
                      )}
                      style={{ maxWidth: "100%" }}
                      min={0}
                      type="number"
                      idRef="activitiesSearchRef"
                      value={values.handoverAmountPercentage}
                      onInputChanged={(e) => {
                        setFieldValue(
                          "handoverAmountPercentage",
                          e.target.value
                        );
                      }}
                    />
                  </Box>
                  {/* Handover Section */}
                  <HandoverSection />
                  <Divider />
                  {/* Fees Section */}
                  <FeesSection />
                  {/* footer action */}
                  <Box className={styles.footerActions}>
                    <CustomButton
                      variant="outlined"
                      color="secondary"
                      onClick={onClose}
                    >
                      {translate("cancel")}
                    </CustomButton>
                    <CustomButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                    >
                      {activeItem && activeItem.propertyPaymentPlanId
                        ? translate("edit-payment-plan")
                        : translate("add-payment-plan")}
                    </CustomButton>
                  </Box>
                </Form>
              </Box>
            </>
          );
        }}
      </Formik>
    </Paper>
  );
};

export default PaymentPlanDialog;
